import { FC } from 'react';
import {
  Container,
  Link,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Link as RouterLink } from 'react-router-dom';

import Meta from '@bizapp-lp/pjr/organisms/Meta';
import { Terms } from '@bizapp-lp/pjr/templates/Terms';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    innerList: {
      '& > li': {
        listStyle: 'none',
        marginLeft: theme.spacing(-4),
      },
    },
    noteList: {
      margin: '8px 0 0 !important',
      padding: '0 !important',
      '& li': {
        position: 'static',
        listStyle: 'none',
        marginTop: '-13px !important',
        marginBottom: '0 !important',
        '&:before': {
          content: "'※'",
          position: 'relative',
          top: '21px',
          left: '-14px',
          fontSize: '14px',
          lineSpacing: '21px',
        },
      },
    },
    lineBreak: {
      '& span': {
        display: 'block',
      },
    },
  }),
);

const title = 'Project Roomサービス仕様書 | 株式会社ワークスアプリケーションズ';
const description =
  '株式会社ワークスアプリケーションズがご提供する、Project Roomサービス仕様書です。';

const TrialSpec: FC = () => {
  const classes = useStyles();

  const termsProps = {
    pageTitle: (
      <>
        <span style={{ display: 'block' }}>HUE Works Suite</span>
        <span style={{ display: 'block' }}>Project Roomサービス仕様書</span>
      </>
    ),
    headerContent: '',
  };
  return (
    <>
      <Meta>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Meta>
      <Terms {...termsProps}>
        <Container disableGutters maxWidth={false}>
          <Typography>
            これは過去のHUE Works Suite Project Roomサービス仕様書です。最新版は
            <Link href="https://www.worksap.co.jp/services/spec/pjr/">
              こちら
            </Link>
            です。
          </Typography>
          <Typography variant="h2">1. 本書の定義</Typography>
          <Typography variant="body1">
            本書は、お客様におけるHUE Works Suite Project Room（以下、Project
            Room）利用にあたり弊社（株式会社ワークスアプリケーションズ・エンタープライズ又は株式会社ワークスアプリケーションズ・システムズのうち、お客様による利用申込に対して承諾した法人をいいます。）より提供可能なクラウドサービスの内容を記述したものです。Project
            Roomの利用に際しては、本内容および「HUE Works Suite / HUE Works
            Suite DX Solutions利用規約」に同意していただく必要があります。
          </Typography>
          <Typography variant="body1">
            本書と「HUE Works Suite / HUE Works Suite DX
            Solutions利用規約」の記載内容が異なる場合には利用規約の内容が優先されます。
          </Typography>
          <Typography variant="body1">
            過去に提示済みのサービス内容と内容が異なる場合には、常に最新のサービス内容が優先して適用されます。
          </Typography>
        </Container>

        <Container disableGutters maxWidth={false}>
          <Typography variant="h2">2. Project Roomサービス概要</Typography>
          <Typography variant="body1">
            Project
            Roomは法人向けプロジェクトコミュニケーションサービスです。コミュニケーションサービスでよく利用される基本機能に加えファイル管理機能を搭載し、チームやプロジェクトでのコラボレーションによるプロジェクト推進の効率化をサポートします。
          </Typography>
          <Typography variant="body1">
            ※Project
            Roomは法人のお客様向けサービスです。ご利用は法人（公的機関を含む）に限らせていただきます。
          </Typography>
          <Typography variant="body1">
            ※Project
            Roomでは外部ユーザーをゲストユーザーとして招待し、共同で利用することが可能です。ただし、当該外部ユーザーによるProject
            Room利用に関連する一切の行為はお客様による行為とみなすこととし、当該外部ユーザーによる利用規約および本サービス仕様書の違反についてはお客様による違反としてお客様が弊社に対して一切の責任を負うものとします。
          </Typography>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.1 標準サービス機能</Typography>
            <Typography variant="body1">
              Project
              Roomでは下記機能を提供いたします。あわせて、初期構築作業（利用環境構築作業および管理者アカウント作成作業）を提供いたします。
            </Typography>
            <Typography variant="body1">
              また、下記機能および初期構築作業の仕様、品質その他一切の内容に関する詳細は、弊社がその裁量によって決定するものとします。
            </Typography>
            <List component="ul">
              <ListItem>
                <ListItemText>管理機能</ListItemText>
                <List component="ul" className={classes.innerList}>
                  <ListItem>
                    <ListItemText>アカウント管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>ゲストアカウント管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>ライセンス管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>権限管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>セキュリティ管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>利用状況モニター</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>監査ログビューアー</ListItemText>
                  </ListItem>
                </List>
              </ListItem>
              <ListItem>
                <ListItemText>ユーザー機能</ListItemText>
                <List component="ul" className={classes.innerList}>
                  <ListItem>
                    <ListItemText>
                      プロジェクト（プロジェクトに関するやりとりをプロジェクトメンバーと実施するためのスペース）作成
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      各プロジェクトのへ招待可能なゲストを制限するためのメールドメインによる許可リスト設定
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      コミュニケーション（フィード投稿、コメント、いいね）
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      ファイル管理（ファイルアップロード、プレビュー、ダウンロード、フォルダ管理）
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>カレンダー（予定登録）</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      ※ゲストユーザーは招待されたプロジェクト内の機能のみ利用できます。権限管理にて管理者権限を付与することや、プロジェクトの管理者になることはできません。
                    </ListItemText>
                  </ListItem>
                </List>
              </ListItem>
            </List>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">
              2.2 環境構築およびアカウントの発行
            </Typography>
            <Typography variant="body1">
              お申し込みから1週間ほどでお申し込み時に登録いただいたメールアドレスあてに、アクセスURLと管理者アカウントの初期ログインIDをお送りいたします。2ユーザー目以降については、管理者アカウントにてお客様ご自身で登録いただけます。
            </Typography>
            <Typography variant="body1">
              ※トライアルサービスを利用されている場合は、トライアルサービス利用にあたり作成したアカウントおよびデータ（以下、データ等）を継続して利用することが可能です。継続利用をご希望の場合には、データ等移行作業のため1日程度環境停止時間をいただきます。トライアルサービスからデータ等を引き継がずに本サービスを利用される場合には、停止時間は伴いません。
            </Typography>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.3 サポートサービス</Typography>
            <Typography variant="body1">
              お申し込みいただきましたメールアドレスあてに機能マニュアル、管理者マニュアルおよび問い合わせ先メールアドレスをお送りいたします。ご不明点がありましたら問い合わせ先メールアドレスまでお問い合わせください。3営業日をめどに１次回答いたします。
            </Typography>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.4 利用料金</Typography>
            <Container disableGutters maxWidth={false}>
              <Typography variant="h4">【標準サービス】</Typography>
              <List component="ul">
                <ListItem>
                  <ListItemText>
                    月額利用料：1,430円(税込）/ライセンス
                  </ListItemText>
                  <List component="ul" className={classes.noteList}>
                    <ListItem>
                      <ListItemText>
                        初回月額利用料に初期構築(利用環境構築)費用を含みます。
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>
                        「契約ライセンス数×10GB」の容量制限があります。容量制限は全ユーザーで利用できる総量となります。
                      </ListItemText>
                    </ListItem>
                  </List>
                </ListItem>
              </List>
              <Typography variant="body1">
                Project
                Roomライセンス数は、一般ユーザーのアカウント（以下、一般アカウント）数、及び、ゲストユーザーのアカウント（以下、ゲストアカウント）数の総数とします。
              </Typography>
              <Typography variant="body1">
                利用料金は、契約ライセンス数に対して発生します。ライセンスの付与状況やライセンス付与したアカウントの状態（有効/無効）は影響しません。
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ライセンス</TableCell>
                      <TableCell>詳細</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        Project Roomライセンス
                      </TableCell>
                      <TableCell>
                        Project
                        Room機能を利用するアカウントに付与するライセンスです。同時にライセンスを付与するアカウント数が契約ライセンス数を超えない限り、ライセンスを付与するアカウントの変更は同一月内に何度でも行うことができます。
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="body1">
                各種管理者は、ライセンスの付与を必要とせず作成することができます。
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>権限（ロール）</TableCell>
                      <TableCell>詳細</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        特権管理者
                      </TableCell>
                      <TableCell>
                        <span style={{ display: 'block' }}>
                          すべての管理者機能を利用できる特権ロールです。
                        </span>
                        <span style={{ display: 'block' }}>
                          特権管理者やアカウント管理者の追加/編集/停止、アプリケーションのライセンス付与をすることができます。
                        </span>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        アカウント管理者
                      </TableCell>
                      <TableCell>
                        一般アカウント、ゲストアカウントの追加/編集/停止およびアカウント管理者の追加/編集/停止、利用アプリケーションのライセンス付与をできる権限です。特権管理者の追加/編集/停止はできません。
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        セキュリティ管理者
                      </TableCell>
                      <TableCell>
                        <span style={{ display: 'block' }}>
                          セキュリティの設定に特化した管理者です。
                        </span>
                        <span style={{ display: 'block' }}>
                          セキュリティポータルで、IPアクセス制御、シングルサインオン、2段階認証の設定を行うことができます。
                        </span>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        Project Room管理者
                      </TableCell>
                      <TableCell>
                        Project Roomの使用状況をモニターすることができます。
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <Typography variant="body1">
                  【例】100ライセンス分の利用契約を締結しており、Project
                  Roomライセンスを付与したアカウントが95、次月から利用開始予定のライセンス未付与のアカウントが10、登録されている。特権管理者、アカウント管理者それぞれ1アカウントずつ登録されており、いずれもライセンスを付与していない。
                </Typography>
                <Typography variant="body1">
                  →登録アカウント数は、95（Project Roomユーザーのアカウント）+
                  10（次月利用開始予定アカウント）+ 1（特権管理者のアカウント）+
                  １（アカウント管理者のアカウント）= 107
                </Typography>
                <Typography variant="body1">
                  付与ライセンス数は、95（Project
                  Roomユーザー）となり、付与ライセンス数（95）が契約ライセンス数（100）を超えないため、契約ライセンス数である100ライセンス分の利用料金が発生します。
                </Typography>
                <Typography variant="body1">
                  この時点においてはProject
                  Roomユーザーの付与ライセンス数が95のため契約ライセンス数の範囲内に収まっていますが、次月から予定どおり10アカウント分についてProject
                  Roomユーザーとしてライセンスの付与が行われた場合、付与ライセンス数は105となり、契約ライセンス数（100）を超えるため、少なくとも超過する5ライセンス分の利用契約を、予め追加で締結いただく必要があります。
                </Typography>
              </TableContainer>
            </Container>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.5 算定方法およびお支払い</Typography>

            <Container disableGutters maxWidth={false}>
              <Typography variant="h4">算定方法</Typography>
              <List component="ul">
                <ListItem>
                  <ListItemText>
                    毎月末時点の契約ライセンス数に応じて翌月1日より1ヶ月分の費用が発生します
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    初月のご請求金額は、日割り計算となります
                  </ListItemText>
                  <List component="ul" className={classes.innerList}>
                    <ListItem>
                      <ListItemText>
                        初月のご請求金額 ＝{'{'}月額利用料－（月額利用料 ×
                        利用しない日数 ÷ 暦日数）{'}'} × 契約ライセンス数
                      </ListItemText>
                    </ListItem>
                  </List>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    ライセンス追加のご請求金額は、日割り計算となります
                  </ListItemText>
                  <List component="ul" className={classes.innerList}>
                    <ListItem>
                      <ListItemText>
                        追加のご請求金額 ＝{'{'}月額利用料－（月額利用料 ×
                        利用しない日数 ÷ 暦日数）{'}'} × 追加契約ライセンス数
                      </ListItemText>
                    </ListItem>
                  </List>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    契約期間の途中で利用契約が終了する場合におけるご請求金額は、日割り計算による減額又は返金は行わず、利用契約の終了日から当該終了日が属する月の末日までの費用も発生します
                  </ListItemText>
                </ListItem>
              </List>
            </Container>

            <Container disableGutters maxWidth={false}>
              <Typography variant="h4">お支払い</Typography>

              <Container disableGutters maxWidth={false}>
                <Typography variant="h5">【請求書支払】</Typography>
                <List component="ul">
                  <ListItem>
                    <ListItemText>毎月末締め、翌月末払い</ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1">
                  ※請求はマネーフォワードケッサイ株式会社による「マネーフォワード
                  ケッサイ」サービスにて行われます
                </Typography>
              </Container>
            </Container>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.6 提供条件</Typography>
            <Typography variant="body1">
              Project Roomの提供に際しては、
              カレンダー機能内のZoom連携において、Zoom Video Communications,
              Inc.（以下「Zoom」といいます。）が提供するサービスが利用されます。お客様は、Zoom連携を利用するにあたり、Zoom所定の「ZOOM
              TERMS OF
              SERVICE」（その名称又は内容に変更が生じた場合には、当該変更後の名称及び内容によるものとします。
              なお、最新版は
              <Link href="https://zoom.us/terms" target="_blank">
                https://zoom.us/terms
              </Link>
              に掲載されています。）のうちEnd
              Userが遵守すべき事項について同意し、これを遵守するものとします。
            </Typography>
          </Container>
        </Container>

        <Container disableGutters maxWidth={false}>
          <Typography variant="h2">付則</Typography>
          <Typography>
            改正後の本仕様書は、2022年3月28日より効力を有するものとします。
          </Typography>
          <Typography className={classes.lineBreak}>
            <span>初版施行日：2021年7月7日</span>
            <span>第１回改正施行日：2021年7月12日</span>
            <span>
              <Link
                component={RouterLink}
                to="/pjr/spec-archive/service-spec-02-20211015"
              >
                第２回改正施行日：2021年10月15日
              </Link>
            </span>
            <span>第３回改正施行日：2022年3月28日</span>
          </Typography>
        </Container>
      </Terms>
    </>
  );
};

export default TrialSpec;
